import type { SpinResponse } from "@slot/client-engine";
import { cannonFire } from "../cannonFixtureHelpers";
import { fx, spinFixture } from "@slot/game-shell";

const mask = (indices: number[]): string =>
  Array.from({ length: 20 }, (_, i) => (indices.includes(i) ? "1" : "0")).join("");

/**
 * Tumbling reels — a LONG (three-step) cascade chain: L2 → L1 → L3 clusters, each winning and
 * removed, with the fourth grid producing no further win. The cannon fires a GROWING multiplier
 * each substep (x2 → x3 → x4, the contiguous CANNON_LADDER); the WinMeter counts 0 → 200 → 500 → 660.
 * Exercises a deeper cascade than reel_tumble_chain. Cluster/mask wins, no payline.
 *
 * EVERY collapse grid is the EXACT gravity result of (previous grid − win mask): survivors keep
 * their identity and only FALL; fresh symbols appear ONLY in the vacated top cells. This is a hard
 * requirement — the tumble mechanic treats the fixture grid as authoritative and REBINDS any
 * survivor whose id disagrees with it, so an arbitrary grid makes surviving symbols visibly morph
 * mid-tumble, silently (the post-run strict pass then matches the rebound grid).
 *
 * Chain geometry: only symbols ABOVE a removed cell can fall. Step 1 clears a bottom block (cols
 * 0,1 drop 2 survivors two rows each). Steps 2 and 3 then win on rows 1-2 — a fresh drop-in sitting
 * on top of a survivor — so each removal still drops the row-0 symbol two cells. The reveal is
 * deliberately low-symbol heavy (19/20 L).
 */
export function baseFixtureReelTumbleLong(): SpinResponse {
  const win1 = mask([10, 11, 15, 16]); // L2 ×4 — bottom-left block
  const win2 = mask([5, 6, 10, 11]);   // L1 ×4 — rows 1-2 of cols 0,1 after the first tumble
  const win3 = mask([5, 6, 10, 11]);   // L3 ×4 — same shape again after the second tumble

  return spinFixture("reel-tumble-long")
    .balance("EUR", "100000")
    // --- seq 0: reveal — L2 cluster wins, removed ---
    .event("reveal", 0, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("200").gridRows([
        ["L1", "L1", "L2", "H1", "L3"],
        ["L5", "L4", "L5", "L2", "L5"],
        ["L2", "L2", "L4", "L5", "L2"],
        ["L2", "L2", "L3", "L4", "L2"],
      ]);

      const s = fx.spinBoundaryStart("base:1", "base");
      e.action(s.type, s.payload, { actionPhaseName: s.phase, debugTag: "start" });

      const w = fx.gridWinHighlight({
        positionMask: win1, winAmount: "200", symbolIdentifier: "L2", symbolCount: "4", // 100 × 2
        lines: [{ positionMask: win1, winAmount: "200", baseWinAmount: "100", multiplierApplied: "2" }],
      });
      e.action(w.type, w.payload, { actionPhaseName: w.phase, isSkippable: true, debugTag: "win1" });

      const fire1 = cannonFire(0);
      e.action(fire1.type, fire1.payload, { actionPhaseName: fire1.phase, debugTag: "cannon_fire1" });

      const r = fx.removeSymbolsByMask(win1);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove1" });

      const t = fx.updateTotalWinMeter("200");
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total1" });
    })
    // --- seq 1: collapse — cols 0,1 survivors fell to rows 2-3 + 2 fresh each; the row-1 drop-ins
    //     land on the surviving L1s at row 2 → L1 cluster wins, removed ---
    .event("collapse", 1, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("500").gridRows([
        ["L3", "L3", "L2", "H1", "L3"],
        ["L1", "L1", "L5", "L2", "L5"],
        ["L1", "L1", "L4", "L5", "L2"],
        ["L5", "L4", "L3", "L4", "L2"],
      ]);

      const w = fx.gridWinHighlight({
        positionMask: win2, winAmount: "300", symbolIdentifier: "L1", symbolCount: "4", // 100 × 3
        lines: [{ positionMask: win2, winAmount: "300", baseWinAmount: "100", multiplierApplied: "3" }],
      });
      e.action(w.type, w.payload, { actionPhaseName: w.phase, isSkippable: true, debugTag: "win2" });

      const fire2 = cannonFire(1);
      e.action(fire2.type, fire2.payload, { actionPhaseName: fire2.phase, debugTag: "cannon_fire2" });

      const r = fx.removeSymbolsByMask(win2);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove2" });

      const t = fx.updateTotalWinMeter("500");
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total2" });
    })
    // --- seq 2: collapse — the row-0 L3s fell to row 2 and fresh L3s dropped onto row 1
    //     → L3 cluster wins, removed ---
    .event("collapse", 2, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("660").gridRows([
        ["L2", "L5", "L2", "H1", "L3"],
        ["L3", "L3", "L5", "L2", "L5"],
        ["L3", "L3", "L4", "L5", "L2"],
        ["L5", "L4", "L3", "L4", "L2"],
      ]);

      const w = fx.gridWinHighlight({
        positionMask: win3, winAmount: "160", symbolIdentifier: "L3", symbolCount: "4", // 40 × 4
        lines: [{ positionMask: win3, winAmount: "160", baseWinAmount: "40", multiplierApplied: "4" }],
      });
      e.action(w.type, w.payload, { actionPhaseName: w.phase, isSkippable: true, debugTag: "win3" });

      const fire3 = cannonFire(2);
      e.action(fire3.type, fire3.payload, { actionPhaseName: fire3.phase, debugTag: "cannon_fire3" });

      const r = fx.removeSymbolsByMask(win3);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove3" });

      const t = fx.updateTotalWinMeter("660");
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total3" });
    })
    // --- seq 3: collapse — no further win, round ends ---
    .event("collapse", 3, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("660").gridRows([
        ["L4", "L1", "L2", "H1", "L3"],
        ["L2", "L4", "L5", "L2", "L5"],
        ["L2", "L5", "L4", "L5", "L2"],
        ["L5", "L4", "L3", "L4", "L2"],
      ]);

      const end = fx.spinBoundaryEnd("base:1", "660");
      e.action(end.type, end.payload, { actionPhaseName: end.phase, debugTag: "end" });
    })
    .build();
}
